// The one transcript column module (docs/09 §4): the unified list renders
// the same defs in both scopes — the Agent column (agent name + hover-reveal
// id) appears only team-wide (`showAgent`), everything else is identical.
// Replaces the duplicated `teamTranscriptColumns` / `transcriptColumns` pair.

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { transcriptStatusTone } from "../../lib/transcriptStatusTone";
import { Badge, Inline, MiniWaveform } from "../../ui";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { CapacityWaitStatus } from "../CapacityWaitStatus";

type TranscriptListItem = components["schemas"]["TranscriptListItem"];

export interface TranscriptTableColumnsOptions {
  // Resolves the team-wide Agent column's ids to display names; an id with no
  // match falls back to a short id. Only read when `showAgent` is true.
  agentNamesById?: ReadonlyMap<string, string>;
  // True on the team-wide (unscoped) list; agent scope hides the column.
  showAgent: boolean;
}

export function transcriptTableColumns({
  agentNamesById,
  showAgent,
}: TranscriptTableColumnsOptions): Array<ColumnDef<TranscriptListItem>> {
  const statusColumn: ColumnDef<TranscriptListItem> = {
    header: "Status",
    cell: ({ row }) => (
      <Inline gap={2}>
        <Badge tone={transcriptStatusTone(row.original.status)}>
          {row.original.status}
        </Badge>
        <CapacityWaitStatus
          isWaitingForCapacity={row.original.is_waiting_for_capacity}
          capacityWaitCause={row.original.capacity_wait_cause}
        />
      </Inline>
    ),
  };
  // Inline audio waveform: a precomputed thumbnail rides each list row
  // (waveform_thumbnail), so it renders with zero extra requests. Absent for
  // text transcripts (and audio whose peaks did not extract) — render nothing.
  const waveformColumn: ColumnDef<TranscriptListItem> = {
    header: "Waveform",
    id: "waveform",
    cell: ({ row }) => {
      const thumbnail = row.original.waveform_thumbnail;
      return thumbnail && thumbnail.length > 0 ? (
        <MiniWaveform peaks={thumbnail} />
      ) : null;
    },
  };
  const agentColumn: ColumnDef<TranscriptListItem> = {
    header: "Agent",
    cell: ({ row }) => (
      <NameWithId
        id={row.original.agent_id}
        idLabel="Agent ID"
        name={agentNamesById?.get(row.original.agent_id)}
      />
    ),
  };
  // UI-LAW §4 DataTable uniformity: sortable columns (source + two dates)
  // carry accessors; Status is a composite (badge + wait) and Agent a
  // name + hover-reveal id, so both stay accessor-free and unsortable. No client
  // facets here — status/source already filter through the URL facets (the
  // FilterBar in the page chrome), and a client facet must never duplicate a
  // URL filter.
  const tailColumns: Array<ColumnDef<TranscriptListItem>> = [
    {
      header: "Source",
      accessorKey: "source",
      // Non-semantic category chip (live | simulated) so the column reads as a
      // design-system chip, not freeform text. Neutral tone: neither source is
      // "healthier", so no semantic color.
      cell: ({ row }) => <Badge>{row.original.source}</Badge>,
    },
    {
      header: "Occurred",
      id: "occurred",
      accessorFn: (transcript) => transcript.occurred_at ?? "",
      // DateCell renders a muted dash when the timestamp is absent.
      cell: ({ row }) => <DateCell value={row.original.occurred_at} />,
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
  ];

  return showAgent
    ? [statusColumn, waveformColumn, agentColumn, ...tailColumns]
    : [statusColumn, waveformColumn, ...tailColumns];
}
